import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, HostBinding } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LicensingApiService } from '@kempoweroyj/ngx-licensing-api-client';

import { AuthService } from 'providers/auth.service';
import { LicenseInfoService } from 'shared/organization-info/license-info.service';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { ServiceToolsCertificationDialogComponent } from 'shared/dialogs/service-tools-certification-dialog/service-tools-certification-dialog.component';
import { IamUsersApiService } from 'api/user.service';

@Component({
    selector: 'ce-banner',
    templateUrl: './banner.component.html',
    styleUrls: ['./banner.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class BannerComponent {
    @Input() @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    showBanner = false;
    bannerType: 'subscription' | 'service-tools' | 'service-tools-warning' | 'service-tools-expiring' | null = null;
    daysUntilSubscriptionExpiry: number | null = null;
    daysUntilCertificationExpiry: number | null = null;
    organizationName: string | null = null;
    licenseName: string | null = null;

    private isOrganizationAdmin = false;
    private isMaintenance = false;

    private readonly SUBSCRIPTION_BANNER_KEY_PREFIX = 'ce-subscription-banner-last-shown-date';
    private readonly SERVICE_TOOLS_BANNER_KEY_PREFIX = 'ce-service-tools-banner-last-shown-date';
    private readonly SERVICE_TOOLS_WARNING_BANNER_KEY_PREFIX = 'ce-service-tools-warning-banner-last-shown-date';
    private readonly SERVICE_TOOLS_EXPIRING_BANNER_KEY_PREFIX = 'ce-service-tools-expiring-banner-last-shown-date';
    private static readonly SERVICE_TOOLS_RESTRICTION_DATE = new Date('2026-03-31T00:00:00.000Z');

    constructor(
        private readonly auth: AuthService,
        private readonly cdRef: ChangeDetectorRef,
        private readonly orgService: OrganizationInfoService,
        private readonly licenseService: LicenseInfoService,
        private readonly licensingApi: LicensingApiService,
        private readonly dialog: MatDialog,
        private readonly iamUsersApi: IamUsersApiService
    ) {
        // Subscribe to organization and license info
        this.orgService.organizationInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((organizationInfo) => {
                this.organizationName =
                    organizationInfo?.impersonated?.tenantName || organizationInfo?.originalTenantName || '';
                this.cdRef.markForCheck();
            });

        this.licenseService.licenseInfo$
            .pipe(
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe((license) => {
                this.licenseName = license?.licenseDefinition?.name || license?.licenseDefinition?.type || '';
                this.cdRef.markForCheck();
            });

        combineLatest([
            this.auth.authenticated$,
            this.auth.permissions$,
            this.orgService.organizationInfo$,
            this.licenseService.licenseInfo$,
        ])
            .pipe(untilDestroyed(this))
            .subscribe(([, , ,]) => {
                this.isOrganizationAdmin = this.auth.getPermissions().isOrganizationAdmin || false;
                this.isMaintenance = this.auth.getPermissions().isMaintenance || false;

                // Check banners: prioritize subscription expiry for org admins, then service tools
                this.checkBanners();
                this.cdRef.markForCheck();
            });
    }

    onDismissBanner(): void {
        this.showBanner = false;
        // Store today's date in localStorage with tenant ID
        const today = new Date().toDateString();
        const storageKey = this.getBannerStorageKey(this.bannerType);
        if (storageKey) {
            localStorage.setItem(storageKey, today);
        }
        this.cdRef.markForCheck();
    }

    openCertificationDialog(): void {
        ServiceToolsCertificationDialogComponent.open(this.dialog);
    }

    private bannerWasShownToday(
        bannerType: 'subscription' | 'service-tools' | 'service-tools-warning' | 'service-tools-expiring'
    ): boolean {
        const storageKey = this.getBannerStorageKey(bannerType);
        if (!storageKey) {
            return false;
        }
        const lastShownDate = localStorage.getItem(storageKey);
        if (!lastShownDate) {
            return false;
        }
        const today = new Date().toDateString();
        return lastShownDate === today;
    }

    private getBannerStorageKey(
        bannerType: 'subscription' | 'service-tools' | 'service-tools-warning' | 'service-tools-expiring'
    ): string | null {
        const tenantId = this.auth.profile?.tenantId;
        if (!tenantId) {
            return null;
        }
        let prefix: string;
        if (bannerType === 'subscription') {
            prefix = this.SUBSCRIPTION_BANNER_KEY_PREFIX;
        } else if (bannerType === 'service-tools') {
            prefix = this.SERVICE_TOOLS_BANNER_KEY_PREFIX;
        } else if (bannerType === 'service-tools-warning') {
            prefix = this.SERVICE_TOOLS_WARNING_BANNER_KEY_PREFIX;
        } else {
            prefix = this.SERVICE_TOOLS_EXPIRING_BANNER_KEY_PREFIX;
        }
        return `${prefix}-${tenantId}`;
    }

    private async checkBanners(): Promise<void> {
        // Reset banner state
        this.showBanner = false;
        this.bannerType = null;
        this.daysUntilSubscriptionExpiry = null;
        this.daysUntilCertificationExpiry = null;

        // Priority 1: Check subscription expiry for org admins (ChargEye only)
        const shouldCheckSubscription =
            this.themeClass !== 'theme-mykempower' &&
            this.isOrganizationAdmin &&
            !this.bannerWasShownToday('subscription');

        if (shouldCheckSubscription) {
            await this.checkSubscriptionExpiry();
            if (this.showBanner) {
                return; // Subscription banner is showing, don't check or show service tools banner
            }
        }

        // Priority 2: Check service tools certification for maintenance users
        const now = new Date();
        const isBeforeRestrictionDate = now < BannerComponent.SERVICE_TOOLS_RESTRICTION_DATE;

        if (
            this.isMaintenance &&
            !this.auth.profile?.impersonated &&
            isBeforeRestrictionDate &&
            !this.bannerWasShownToday('service-tools-warning')
        ) {
            // Show warning banner before restriction date
            await this.checkServiceToolsCertificationWarning();
        } else if (this.isMaintenance && !this.auth.profile?.impersonated && !isBeforeRestrictionDate) {
            // After restriction date: check expiring banner first, then removal banner
            if (!this.bannerWasShownToday('service-tools-expiring')) {
                await this.checkServiceToolsCertificationExpiring();
            }
            if (!this.showBanner && !this.bannerWasShownToday('service-tools')) {
                await this.checkServiceToolsCertification();
            }
        }
    }

    private async checkSubscriptionExpiry(): Promise<void> {
        try {
            // Fetch all licenses including future ones
            const response = await this.licensingApi.getActiveLicenses({ includeFuture: true });
            const allLicenses = response?.licenses || [];

            // Filter to ChargEye subscriptions (non-MyKempower), sorted by start date
            const chargeEyeLicenses = allLicenses
                .filter((l) => !l.type.startsWith('mykempower'))
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

            if (chargeEyeLicenses.length === 0) {
                return;
            }

            // Find currently active license
            // Note: This will return undefined if all licenses start in the future
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);
            const todayTime = today.getTime();

            const activeLicense = chargeEyeLicenses.find((l) => {
                const startDate = new Date(l.startDate);
                startDate.setUTCHours(0, 0, 0, 0);
                const endDate = l.endDate ? new Date(l.endDate) : null;
                if (endDate) {
                    endDate.setUTCHours(23, 59, 59, 999); // End of day
                }

                const hasStarted = startDate.getTime() <= todayTime;
                const hasNotEnded = !endDate || endDate.getTime() >= todayTime;

                return hasStarted && hasNotEnded;
            });

            if (!activeLicense || !activeLicense.endDate) {
                return; // No currently active license or license is permanent
            }

            // Calculate days until expiry
            const endDate = new Date(activeLicense.endDate);
            endDate.setUTCHours(0, 0, 0, 0);

            const diffTime = endDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Check if within threshold (30 days)
            if (diffDays < 0 || diffDays > 30) {
                return;
            }

            // Check if there's a following subscription
            const followingLicense = chargeEyeLicenses.find((l) => {
                const startDate = new Date(l.startDate);
                startDate.setUTCHours(0, 0, 0, 0);
                return startDate.getTime() > endDate.getTime();
            });

            if (!followingLicense) {
                // No following subscription - show warning
                this.daysUntilSubscriptionExpiry = diffDays;
                this.showBanner = true;
                this.bannerType = 'subscription';
                this.cdRef.markForCheck();
                return;
            }

            // Check gap between current and following subscription
            const nextStartDate = new Date(followingLicense.startDate);
            nextStartDate.setUTCHours(0, 0, 0, 0);

            // Calculate the day after current subscription ends
            const dayAfterEnd = new Date(endDate);
            dayAfterEnd.setUTCDate(dayAfterEnd.getUTCDate() + 1);
            dayAfterEnd.setUTCHours(0, 0, 0, 0);

            // Check if gap is more than 0 days
            const gapDays = Math.floor((nextStartDate.getTime() - dayAfterEnd.getTime()) / (1000 * 60 * 60 * 24));

            if (gapDays > 0) {
                // Gap exists - show warning
                this.daysUntilSubscriptionExpiry = diffDays;
                this.showBanner = true;
                this.bannerType = 'subscription';
            }

            this.cdRef.markForCheck();
        } catch (error) {
            console.error('Error checking subscription expiry:', error);
        }
    }

    private async checkServiceToolsCertificationWarning(): Promise<void> {
        await this.checkServiceToolsCertificationStatus('service-tools-warning');
    }

    private async checkServiceToolsCertification(): Promise<void> {
        await this.checkServiceToolsCertificationStatus('service-tools');
    }

    private async checkServiceToolsCertificationExpiring(): Promise<void> {
        try {
            const certificationInfo = await this.getCertificationInfo();
            if (!certificationInfo) {
                return;
            }

            const { hasCertification, certificationDate } = certificationInfo;

            if (!hasCertification) {
                return; // No certification - will be handled by removal banner
            }

            // Check if certification expires within 30 days
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);

            const diffTime = certificationDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            // Show banner if expiring within 30 days (but not yet expired)
            if (diffDays >= 0 && diffDays <= 30) {
                this.daysUntilCertificationExpiry = diffDays;
                this.showBanner = true;
                this.bannerType = 'service-tools-expiring';
                this.cdRef.markForCheck();
            }
        } catch (error) {
            console.error('Error checking service tools certification expiring:', error);
        }
    }

    private async checkServiceToolsCertificationStatus(
        bannerType: 'service-tools' | 'service-tools-warning'
    ): Promise<void> {
        try {
            const certificationInfo = await this.getCertificationInfo();
            if (!certificationInfo) {
                return;
            }

            const { hasCertification, certificationDate } = certificationInfo;

            // No certification at all
            if (!hasCertification) {
                // Show warning banner before restriction date, removal banner after
                this.showBanner = true;
                this.bannerType = bannerType;
                this.cdRef.markForCheck();
                return;
            }

            // Check if certification is expired
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);
            const isExpired = certificationDate.getTime() < today.getTime();

            if (isExpired) {
                // Certification is expired - show appropriate banner
                this.showBanner = true;
                this.bannerType = bannerType;
                this.cdRef.markForCheck();
            }
        } catch (error) {
            console.error(`Error checking service tools certification status (${bannerType}):`, error);
        }
    }

    /**
     * Shared helper to get user's certification info
     * Returns null if user/tenant info is unavailable, otherwise returns certification details
     */
    private async getCertificationInfo(): Promise<{
        hasCertification: boolean;
        certificationDate: Date | null;
    } | null> {
        const userId = this.auth.profile?.sub;
        const tenantId = this.auth.profile?.tenantId;

        if (!userId || !tenantId) {
            return null;
        }

        // Strip auth0| prefix if present
        const cleanUserId = userId.includes('|') ? userId.split('|')[1] : userId;

        // Fetch user data including skills
        const user = await this.iamUsersApi.getAdmin(cleanUserId, tenantId).pipe(take(1)).toPromise();

        if (!user) {
            return null;
        }

        const hasCertification = !!user.skills?.maintenance?.expiresAt;
        const certificationDate = hasCertification ? new Date(user.skills.maintenance.expiresAt) : null;
        if (certificationDate) {
            certificationDate.setUTCHours(0, 0, 0, 0);
        }

        return { hasCertification, certificationDate };
    }
}
